// @ts-nocheck
import React, {useEffect, useRef, useState} from 'react';

import * as d3 from 'd3';
import i18n from 'i18next';
import {generatePath, useHistory} from 'react-router-dom';
import styled from 'styled-components';

import {ROUTES} from '@app/routing';
import {colors} from '@app/styles/theme';

import {EMPTY_VALUE} from '@consts/common';
import {defineActivePath} from '@entities/relations/activePath';
import {DeclarationGapTypeLabel, TaxpayerPayStatusLabel} from '@entities/relations/constants';
import {isDepthLimitReached, MAX_VAT_TREE_LEVEL} from '@entities/relations/depth';
import {
    getRiskBackgroundColor,
    getRiskColor,
    getRiskTextColor,
    numGroup,
} from '@entities/relations/utils';
import {
    getDeclarationTypeIcon,
    getDeclarationTypeLabelKey,
    getTaxpayerTypeKind,
    getTaxpayerTypeLabelKey,
} from '@pages/Relations/helpers/cardPresentation';

const Wrapper = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    min-height: 460px;
    overflow: hidden;

    .chart-toggle {
        position: absolute;
        top: 16px;
        left: 16px;
        z-index: 2;
        border: 1px solid ${colors.white};
        background: rgba(29, 34, 41, 0.78);
        color: ${colors.white};
        border-radius: 8px;
        padding: 12px 16px;
        font-size: 12px;
        font-weight: 600;
        cursor: pointer;
    }
`;

const createGraph = (mode, onZoomChange) => {
    const compact = mode === 'simple';
    const card = compact
        ? {width: 100, height: 48, horizontalSpace: 48, verticalSpace: 120, zoom: 1}
        : {width: 300, height: 223, horizontalSpace: 64, verticalSpace: 160, zoom: 0.55};

    const width = 1320;
    const height = 460;
    const margin = {top: compact ? 50 : -50, right: 0, bottom: 0, left: (width - card.width) / 2};
    const svg = d3
        .create('svg')
        .attr('class', 'host')
        .attr('width', '100%')
        .attr('height', '100%')
        .attr('viewBox', [0, 0, width, height])
        .style('font-family', 'Inter, sans-serif')
        .style('user-select', 'none')
        .attr('cursor', 'grab');

    const defs = svg.append('defs');
    defs.append('pattern')
        .attr('id', 'dotted-bg')
        .attr('width', 21)
        .attr('height', 21)
        .attr('patternUnits', 'userSpaceOnUse')
        .append('circle')
        .attr('cx', 2)
        .attr('cy', 2)
        .attr('r', 2)
        .attr('fill', '#E5E5E5');
    const marker = defs
        .append('marker')
        .attr('id', 'arrow')
        .attr('viewBox', [0, 0, 16, 20])
        .attr('refX', 8)
        .attr('refY', 5)
        .attr('markerWidth', 10)
        .attr('markerHeight', 16)
        .attr('markerUnits', 'userSpaceOnUse')
        .attr('orient', 'auto-start-reverse');
    marker
        .append('path')
        .attr(
            'd',
            d3.line()([
                [1, 1],
                [15, 5],
                [1, 9],
                [1, 1],
            ]),
        )
        .attr('stroke', '#838383')
        .attr('fill', '#838383');
    svg.append('rect')
        .attr('class', 'movable')
        .attr('width', 100000)
        .attr('height', 100000)
        .attr('x', -50000)
        .attr('y', -50000)
        .attr('fill', 'url(#dotted-bg)');
    const duplicateLinks = svg
        .append('g')
        .attr('class', 'movable')
        .attr('fill', 'none')
        .attr('pointer-events', 'none');
    const nodes = svg.append('g').attr('class', 'movable');
    const links = svg
        .append('g')
        .attr('class', 'movable')
        .attr('fill', 'none');

    const diagonal = d3
        .linkVertical()
        .y((d) => margin.top + d.y + card.height)
        .x((d) => margin.left + d.x + card.width / 2);

    const zoom = d3
        .zoom()
        .scaleExtent([0.1, 8])
        .on('zoom', (e) => {
            svg.attr('cursor', 'grabbing');
            svg.selectAll('.movable').attr('transform', e.transform);
            onZoomChange?.(e.transform);
        })
        .on('end', () => svg.attr('cursor', 'grab'));

    svg.call(zoom).on('dblclick.zoom', null);

    return {
        svg,
        zoom,
        duplicateLinks,
        links,
        nodes,
        diagonal,
        card,
        margin,
        compact,
        width,
        height,
    };
};

const taxpayerTypeIcon = (node, data, compact) => {
    const color = getRiskColor(data.properties?.surColour);
    const kind = getTaxpayerTypeKind(data.properties?.transitOrGap);

    if (kind === 'square') {
        node.append('rect')
            .attr('x', compact ? 16 : 9.5)
            .attr('y', compact ? 15 : 9.5)
            .attr('width', compact ? 18 : 29)
            .attr('height', compact ? 18 : 29)
            .attr('fill', color);
        return;
    }

    if (kind === 'triangle') {
        node.append('path')
            .attr('transform', `translate(${compact ? 16 : 8}, ${compact ? 15 : 8})`)
            .attr('d', compact ? 'M9 0L18 18H0L9 0Z' : 'M16 0L32 32H0L16 0Z')
            .attr('fill', color);
        return;
    }

    if (kind === 'circle') {
        node.append('circle')
            .attr('cx', compact ? 25 : 24.5)
            .attr('cy', compact ? 24 : 24.5)
            .attr('r', compact ? 9 : 16.5)
            .attr('fill', color);
        return;
    }

    node.append('path')
        .attr('d', 'M1 1H13.5858L1 13.5858V1ZM15 15H2.41421L15 2.41421V15Z')
        .attr('stroke', '#939AA4')
        .attr(
            'transform',
            `translate(${compact ? 16 : 9.5}, ${compact ? 15 : 9.5}) scale(${
                compact ? 1.12 : 1.8
            })`,
        )
        .attr('fill', '#fff')
        .attr('stroke-width', 2);
};

const declarationTypeIcon = (node, x, y, type) => {
    node.append('image')
        .attr('href', getDeclarationTypeIcon(type))
        .attr('x', x)
        .attr('y', y)
        .attr('width', 20)
        .attr('height', 20)
        .attr('preserveAspectRatio', 'xMidYMid meet');
};

const getNoDataText = () => i18n.t('no_data');

const isMissingRelationValue = (value) => value === undefined || value === null || value === '';

const formatRelationValue = (value, options) => {
    if (isMissingRelationValue(value)) return getNoDataText();
    return numGroup(Number(value), options);
};

const detailRows = (data) => [
    [
        'page_relations.card.pay_status',
        i18n.t(
            TaxpayerPayStatusLabel[data.properties?.payStatus] ||
                'page_relations.pay_status.not_paid',
        ),
    ],
    ['page_relations.card.to_pay', formatRelationValue(data.properties?.sumPayNds)],
    ['page_relations.card.to_return', formatRelationValue(data.properties?.sumNdsDeduction)],
    ['page_relations.card.total', formatRelationValue(data.properties?.totalSumNds)],
    ['page_relations.card.gap_sum', formatRelationValue(data.properties?.gapSum)],
];

const getDeclarationTypeText = (declarationType) => {
    const key = getDeclarationTypeLabelKey(declarationType);
    return key ? i18n.t(key) : EMPTY_VALUE;
};

const getTaxpayerTypeText = (transitOrGap) => i18n.t(getTaxpayerTypeLabelKey(transitOrGap));

const TRANSITION_MS = 250;
const DETAIL_CARD_HEIGHT = 223;
const DETAIL_EXPANDED_MAIN_HEIGHT = 300;
const DETAIL_EXPANDED_EXTRA = DETAIL_EXPANDED_MAIN_HEIGHT - (DETAIL_CARD_HEIGHT - 48);
const SHOW_MORE_TRANSITION_MS = 360;

const createTransition = (svg, duration = TRANSITION_MS) =>
    svg
        .transition()
        .duration(duration)
        .ease(d3.easeCubicInOut);

const appendShadowRect = (node, {x = 0, y = 0, width, height, rx = 8, ry = 8}) =>
    node
        .append('rect')
        .attr('class', 'shadow-bg')
        .attr('x', x + 4)
        .attr('y', y + 4)
        .attr('width', width)
        .attr('height', height)
        .attr('rx', rx)
        .attr('ry', ry)
        .attr('fill', '#c0c8d1');

const getDetailMainHeight = (isExpanded) =>
    isExpanded ? DETAIL_EXPANDED_MAIN_HEIGHT : DETAIL_CARD_HEIGHT - 48;

const getDefaultZoomTransform = (graph) =>
    d3.zoomIdentity
        .scale(graph.card.zoom)
        .translate(
            graph.compact
                ? -((graph.width - graph.card.width) / 2 - graph.margin.left)
                : graph.width / 2,
            graph.margin.top + graph.card.height,
        );

const tweenNumberAttr = (attrName, to) =>
    function() {
        const from = Number(d3.select(this).attr(attrName)) || 0;
        const interpolate = d3.interpolateNumber(from, to);
        return (t) => String(interpolate(t));
    };

const getNodeTopOffset = (node, compact) => (!compact && node.data.properties?.isTaxGap ? -38 : 0);

const getNodeBottomOffset = (node, compact, expandedCards) => {
    if (compact) return 48;
    const badgeHeight = node.data.properties?.isTaxGap ? 86 : 48;
    const mainHeight = getDetailMainHeight(Boolean(expandedCards[node.data.uuid]));
    return getNodeTopOffset(node, compact) + badgeHeight + mainHeight;
};

const createLinkPath = ({graph, source, target, expandedCards}) =>
    d3
        .linkVertical()
        .x((point) => graph.margin.left + point.x + graph.card.width / 2)
        .y((point) => graph.margin.top + point.y)({
        source: {
            x: source.x,
            y: source.y + getNodeBottomOffset(source, graph.compact, expandedCards),
        },
        target: {
            x: target.x,
            y: target.y + getNodeTopOffset(target, graph.compact),
        },
    });

const applyExpandedLayout = (root, expandedCards, compact) => {
    const walk = (node, offset = 0) => {
        node.renderY = node.y + offset;
        const nextOffset =
            offset + (!compact && expandedCards[node.data.uuid] ? DETAIL_EXPANDED_EXTRA : 0);
        node.children?.forEach((child) => walk(child, nextOffset));
    };

    walk(root);
};

const hasSavedPosition = (node) => node?.data?.x0 !== undefined && node?.data?.y0 !== undefined;

const getSavedLinkPath = ({graph, link, expandedCards}) =>
    createLinkPath({
        graph,
        expandedCards,
        source: {
            ...link.source,
            x: link.source.data.x0 ?? link.source.x,
            y: link.source.data.y0 ?? link.source.y,
        },
        target: {
            ...link.target,
            x: link.target.data.x0 ?? link.target.x,
            y: link.target.data.y0 ?? link.target.y,
        },
    });

const getFinalLinkPath = ({graph, link, expandedCards}) =>
    createLinkPath({
        graph,
        expandedCards,
        source: {...link.source, y: link.source.renderY ?? link.source.y},
        target: {...link.target, y: link.target.renderY ?? link.target.y},
    });

const getCollapsedLinkPath = ({graph, link, expandedCards, parentNode}) =>
    createLinkPath({
        graph,
        expandedCards,
        source: {...link.source, y: link.source.renderY ?? link.source.y},
        target: {
            ...link.target,
            x: parentNode.x,
            y: parentNode.renderY ?? parentNode.y,
            renderY: parentNode.renderY ?? parentNode.y,
        },
    });

const createDuplicateLinkPath = ({graph, source, target, expandedCards}) =>
    d3
        .linkVertical()
        .x((point) => graph.margin.left + point.x + graph.card.width / 2)
        .y((point) => graph.margin.top + point.y)({
        source: {
            x: source.x,
            y:
                (source.renderY ?? source.y) +
                getNodeBottomOffset(source, graph.compact, expandedCards),
        },
        target: {
            x: target.x,
            y:
                (target.renderY ?? target.y) +
                getNodeBottomOffset(target, graph.compact, expandedCards),
        },
    });

const getDuplicateLinks = (nodes) => {
    const pendingNodes = [...nodes];
    const duplicateLinks = [];

    while (pendingNodes.length) {
        const node = pendingNodes.shift();
        if (!node?.data?.elementId) continue;

        pendingNodes
            .filter(
                (candidate) =>
                    candidate.data.elementId === node.data.elementId &&
                    candidate.depth !== node.depth,
            )
            .forEach((duplicate) => {
                duplicateLinks.push({
                    id: `${node.data.uuid}-${duplicate.data.uuid}`,
                    source: node,
                    target: duplicate,
                });
            });
    }

    return duplicateLinks;
};

const linkIsActive = (link) => link.source.data.isActive && link.target.data.isActive;

export const RelationsChart = ({data, cardDirection, onNodeToggle}) => {
    const containerRef = useRef(null);
    const history = useHistory();
    const chartStateRef = useRef(null);
    const zoomTransformRef = useRef(null);
    const lastToggledCardRef = useRef<string | number | null>(null);
    const pendingShowMoreFocusRef = useRef<{
        mode: 'expand' | 'collapse';
        targetId: string | number;
    } | null>(null);
    const pendingBranchFocusRef = useRef<{
        mode: 'expand' | 'collapse';
        targetId: string | number;
    } | null>(null);
    const pendingNodeIdsRef = useRef(new Set<string | number>());
    const [mode, setMode] = useState('simple');
    const [branchRevision, setBranchRevision] = useState(0);
    const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>({});

    const animateBranchCollapse = React.useCallback(
        async (clickedNode) => {
            const state = chartStateRef.current;
            if (!state) return;

            const {graph, links, node, duplicateLinks} = state;
            const collapseIds = new Set(
                clickedNode
                    .descendants()
                    .slice(1)
                    .map((item) => item.data.uuid),
            );

            if (!collapseIds.size) return;

            const transition = createTransition(graph.svg);
            const collapsingNodes = node.filter((d) => collapseIds.has(d.data.uuid));
            const collapsingLinks = links.filter((d) => collapseIds.has(d.target.data.uuid));
            const collapsingDuplicateLinks = duplicateLinks.filter(
                (d) => collapseIds.has(d.source.data.uuid) || collapseIds.has(d.target.data.uuid),
            );

            await Promise.all([
                collapsingNodes
                    .transition(transition)
                    .attr(
                        'transform',
                        () =>
                            `translate(${graph.margin.left + clickedNode.x}, ${graph.margin.top +
                                (clickedNode.renderY ?? clickedNode.y)})`,
                    )
                    .attr('fill-opacity', 0)
                    .attr('stroke-opacity', 0)
                    .end(),
                collapsingLinks
                    .transition(transition)
                    .attr('stroke-opacity', 0)
                    .attr('d', (d) =>
                        getCollapsedLinkPath({
                            graph,
                            link: d,
                            expandedCards,
                            parentNode: clickedNode,
                        }),
                    )
                    .end(),
                collapsingDuplicateLinks
                    .transition(transition)
                    .style('opacity', 0)
                    .end(),
            ]);
        },
        [expandedCards],
    );

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return undefined;
        if (!data) return undefined;

        const previousSvg = container.querySelector('svg.host');

        defineActivePath(data, cardDirection);

        const graph = createGraph(mode, (transform) => {
            zoomTransformRef.current = transform;
        });
        const tree = d3
            .tree()
            .nodeSize([
                graph.card.width + graph.card.horizontalSpace,
                graph.card.height + graph.card.verticalSpace,
            ]);
        const root = d3.hierarchy(data);
        root.descendants().forEach((item) => {
            item._id = item.data.uuid;
        });
        tree(root);
        applyExpandedLayout(root, expandedCards, graph.compact);
        const duplicateLinkData = getDuplicateLinks(root.descendants());
        const transition = createTransition(graph.svg, SHOW_MORE_TRANSITION_MS);

        const duplicateLinks = graph.duplicateLinks
            .selectAll('path.dup')
            .data(duplicateLinkData)
            .enter()
            .append('path');

        duplicateLinks
            .attr('class', 'dup')
            .attr('stroke-width', 1)
            .attr('stroke', '#4F79D0')
            .attr('stroke-linecap', 'round')
            .attr('stroke-dasharray', '20')
            .attr('fill', 'none')
            .style('opacity', 0)
            .attr('d', (d) =>
                createDuplicateLinkPath({
                    graph,
                    source: d.source,
                    target: d.target,
                    expandedCards,
                }),
            )
            .transition(transition)
            .style('opacity', 1)
            .attr('d', (d) =>
                createDuplicateLinkPath({
                    graph,
                    source: d.source,
                    target: d.target,
                    expandedCards,
                }),
            );

        const links = graph.links
            .selectAll('path')
            .data(root.links())
            .enter()
            .append('path');

        links
            .attr('stroke', (d) => (linkIsActive(d) ? '#59606A' : '#838383'))
            .attr('stroke-width', (d) => (linkIsActive(d) ? 3 : 1))
            .attr('stroke-opacity', (d) => (hasSavedPosition(d.target) ? 1 : 0))
            .attr('marker-start', (d) => (d.target.data.relationshipToParent ? 'url(#arrow)' : ''))
            .attr('marker-end', (d) => (d.target.data.relationshipFromParent ? 'url(#arrow)' : ''))
            .attr('d', (d) =>
                hasSavedPosition(d.target)
                    ? getSavedLinkPath({graph, link: d, expandedCards})
                    : getFinalLinkPath({graph, link: d, expandedCards}),
            )
            .transition(transition)
            .attr('stroke-opacity', 1)
            .attr('d', (d) => getFinalLinkPath({graph, link: d, expandedCards}));
        links
            .append('title')
            .text(
                (d) =>
                    `${i18n.t('page_relations.tooltip.income')}: ${formatRelationValue(
                        d.target.data.relationshipFromParent?.properties?.taxAmount,
                    )}\n${i18n.t('page_relations.tooltip.outcome')}: ${formatRelationValue(
                        d.target.data.relationshipToParent?.properties?.taxAmount,
                    )}`,
            );

        const highlightConnections = (hoveredNode) => {
            const isConnected = (link) =>
                [link.source, link.target].some(
                    (endpoint) =>
                        endpoint.data.uuid === hoveredNode.data.uuid ||
                        (hoveredNode.data.elementId &&
                            endpoint.data.elementId === hoveredNode.data.elementId),
                );

            [links, duplicateLinks].forEach((selection) => {
                selection
                    .interrupt('highlight')
                    .transition('highlight')
                    .duration(200)
                    .ease(d3.easeCubicOut)
                    .style('stroke', (link) =>
                        hoveredNode && isConnected(link) ? colors.brandPrimary : null,
                    )
                    .style('stroke-width', (link) => (hoveredNode && isConnected(link) ? 4 : null))
                    .style('stroke-opacity', (link) =>
                        hoveredNode ? (isConnected(link) ? 1 : 0.2) : null,
                    );
            });
        };

        const node = graph.nodes
            .selectAll('g.chart-card')
            .data(root.descendants())
            .enter()
            .append('g')
            .attr('class', 'chart-card')
            .on('mouseenter', (_, d) => highlightConnections(d))
            .on('mouseleave', () => highlightConnections(null))
            .attr(
                'transform',
                (d) =>
                    `translate(${graph.margin.left +
                        (hasSavedPosition(d) ? d.data.x0 : d.x)}, ${graph.margin.top +
                        (hasSavedPosition(d) ? d.data.y0 : d.renderY ?? d.y)})`,
            )
            .attr('fill-opacity', (d) => (hasSavedPosition(d) ? 1 : 0))
            .attr('stroke-opacity', (d) => (hasSavedPosition(d) ? 1 : 0))
            .on('click', async (_, d) => {
                if (d.data.isNonNpd) return;
                if (isDepthLimitReached(d)) return;
                if (pendingNodeIdsRef.current.has(d.data.uuid)) return;

                pendingNodeIdsRef.current.add(d.data.uuid);
                try {
                    const isExpanding = !d.data.children || d.data.forExpand;
                    pendingBranchFocusRef.current = {
                        mode: isExpanding ? 'expand' : 'collapse',
                        targetId: d.data.uuid,
                    };
                    if (d.data.children && !d.data.forExpand) {
                        await animateBranchCollapse(d);
                    }
                    await onNodeToggle(d);
                    setBranchRevision((prev) => prev + 1);
                } finally {
                    pendingNodeIdsRef.current.delete(d.data.uuid);
                }
            });

        node.transition(transition)
            .attr(
                'transform',
                (d) =>
                    `translate(${graph.margin.left + d.x}, ${graph.margin.top +
                        (d.renderY ?? d.y)})`,
            )
            .attr('fill-opacity', 1)
            .attr('stroke-opacity', 1);

        if (graph.compact) {
            node.each(function(d) {
                const el = d3.select(this);
                appendShadowRect(el, {width: 100, height: 48});
                el.append('rect')
                    .attr('width', 100)
                    .attr('height', 48)
                    .attr('rx', 8)
                    .attr('ry', 8)
                    .attr('fill', '#fff')
                    .attr('stroke', colors.border);
                el.append('path')
                    .attr('d', 'M1 0V32')
                    .attr('transform', 'translate(48, 8)')
                    .attr('stroke', '#E1E8EF');
                taxpayerTypeIcon(el, d.data, true);
                declarationTypeIcon(el, 64, 14, d.data.properties?.declarationType);

                if (d.parent) {
                    const rel =
                        cardDirection === 'SALE'
                            ? d.data.relationshipFromParent
                            : d.data.relationshipToParent;
                    const top = el.append('g').attr('transform', 'translate(-18, -45)');
                    top.append('rect')
                        .attr('width', 136)
                        .attr('height', 28)
                        .attr('rx', 4)
                        .attr('ry', 4)
                        .attr('fill', '#fff')
                        .attr('stroke', colors.border);
                    top.append('text')
                        .attr('x', 10)
                        .attr('y', 18)
                        .attr('font-size', 12)
                        .text(
                            `${i18n.t('page_relations.tax')} ${formatRelationValue(
                                rel?.properties?.taxAmount,
                            )}`,
                        );
                }

                if (d.data.properties?.isTaxGap) {
                    const bottom = el.append('g').attr('transform', 'translate(-15, 56)');
                    bottom
                        .append('rect')
                        .attr('width', 130)
                        .attr('height', 28)
                        .attr('rx', 4)
                        .attr('ry', 4)
                        .attr('fill', '#fff')
                        .attr('stroke', colors.border);
                    bottom
                        .append('path')
                        .attr('transform', 'translate(2, 2)')
                        .attr('d', 'M5.5 13L17.5 3L13.5 11H19.5L7.5 21L11.5 13H5.5Z')
                        .attr('stroke', colors.danger)
                        .attr('stroke-width', 2)
                        .attr('fill', 'none');
                    bottom
                        .append('text')
                        .attr('x', 74)
                        .attr('y', 18)
                        .attr('text-anchor', 'middle')
                        .attr('fill', colors.danger)
                        .text(formatRelationValue(d.data.properties.gapSum, {fractionDigits: 0}));
                }
            });
        } else {
            node.each(function(d) {
                const el = d3.select(this);
                const isExpanded = expandedCards[d.data.uuid];
                const badgeColor = d.data.properties?.isTaxGap
                    ? getRiskBackgroundColor('RED')
                    : getRiskBackgroundColor(d.data.properties?.surColour);
                const badgeTextColor = d.data.properties?.isTaxGap
                    ? getRiskTextColor('RED')
                    : getRiskTextColor(d.data.properties?.surColour);

                appendShadowRect(el, {
                    width: 300,
                    height: d.data.properties.isTaxGap ? 86 : 48,
                    y: d.data.properties.isTaxGap ? -38 : 0,
                });
                el.append('rect')
                    .attr('width', 300)
                    .attr('height', d.data.properties.isTaxGap ? 86 : 48)
                    .attr('y', d.data.properties.isTaxGap ? -38 : 0)
                    .attr('rx', 8)
                    .attr('ry', 8)
                    .attr('fill', badgeColor)
                    .attr('stroke', colors.border);
                taxpayerTypeIcon(el, d.data, false);
                el.append('text')
                    .attr('x', 46)
                    .attr('y', d.data.properties.isTaxGap ? -14 : 30)
                    .attr('fill', badgeTextColor)
                    .attr('font-size', 14)
                    .attr('font-weight', 600)
                    .text(getTaxpayerTypeText(d.data.properties?.transitOrGap));
                if (d.data.properties.isTaxGap) {
                    el.append('text')
                        .attr('x', 42)
                        .attr('y', 10)
                        .attr('fill', badgeTextColor)
                        .attr('font-size', 14)
                        .attr('font-weight', 600)
                        .text(formatRelationValue(d.data.properties.gapSum));
                    el.append('text')
                        .attr('x', 42)
                        .attr('y', 30)
                        .attr('fill', badgeTextColor)
                        .attr('font-size', 14)
                        .attr('font-weight', 600)
                        .text(
                            i18n.t(
                                DeclarationGapTypeLabel[d.data.properties.gapType] ||
                                    'terms.declaration.gap_type.undefined',
                            ),
                        );
                } else {
                    const sumPayNds = d.data.properties.sumPayNds;
                    const sumNdsDeduction = d.data.properties.sumNdsDeduction;
                    const deductionShare = Number(sumPayNds)
                        ? (Number(sumNdsDeduction) / Number(sumPayNds)) * 100
                        : 0;
                    el.append('text')
                        .attr('x', 222)
                        .attr('y', 30)
                        .attr('fill', badgeTextColor)
                        .attr('font-size', 14)
                        .attr('font-weight', 600)
                        .text(
                            isMissingRelationValue(sumPayNds) ||
                                isMissingRelationValue(sumNdsDeduction)
                                ? getNoDataText()
                                : `${numGroup(deductionShare, {fractionDigits: 0})} %`,
                        );
                }

                const main = el
                    .append('g')
                    .attr('class', 'main')
                    .attr('transform', 'translate(0, 48)');
                appendShadowRect(main, {
                    width: 300,
                    height: getDetailMainHeight(isExpanded),
                });
                main.append('rect')
                    .attr('class', 'bg')
                    .attr('width', 300)
                    .attr('height', getDetailMainHeight(isExpanded))
                    .attr('rx', 8)
                    .attr('ry', 8)
                    .attr('fill', '#fff')
                    .attr('stroke', colors.border);
                main.append('text')
                    .attr('x', 16)
                    .attr('y', 28)
                    .attr('fill', colors.brandPrimary)
                    .attr('font-size', 18)
                    .attr('font-weight', 600)
                    .style('cursor', d.data.properties?.tin ? 'pointer' : 'default')
                    .text(d.data.properties?.name || EMPTY_VALUE)
                    .on('click', (event) => {
                        event.stopPropagation();
                        const tin = d.data.properties?.tin;
                        if (!tin) return;

                        history.push(generatePath(ROUTES.TAXPAYER_DETAILS, {tin}));
                    });
                main.append('text')
                    .attr('x', 16)
                    .attr('y', 56)
                    .attr('fill', colors.textPrimary)
                    .attr('font-size', 14)
                    .text(`${i18n.t('terms.tin')} ${d.data.properties?.tin || EMPTY_VALUE}`);
                main.append('path')
                    .attr('d', 'M1 1H268')
                    .attr('transform', 'translate(16, 80)')
                    .attr('stroke', '#E1E8EF');
                main.append('text')
                    .attr('x', 16)
                    .attr('y', 110)
                    .attr('fill', colors.textPrimary)
                    .attr('font-size', 12)
                    .attr('font-weight', 600)
                    .text(i18n.t('page_relations.card.dec_type'));
                main.append('text')
                    .attr('x', 284)
                    .attr('y', 110)
                    .attr('text-anchor', 'end')
                    .attr('fill', colors.textPrimary)
                    .attr('font-size', 12)
                    .attr('font-weight', 600)
                    .text(getDeclarationTypeText(d.data.properties?.declarationType));
                const toggle = main
                    .append('text')
                    .attr('class', 'card-toggle-link')
                    .attr('text-anchor', 'middle')
                    .attr('transform', `translate(150, ${isExpanded ? 280 : 150})`)
                    .attr('fill', colors.brandPrimary)
                    .attr('font-size', 14)
                    .attr('font-weight', 500)
                    .style('cursor', 'pointer')
                    .text(
                        i18n.t(
                            isExpanded
                                ? 'page_relations.card.hide'
                                : 'page_relations.card.show_more',
                        ),
                    );

                toggle.on('click', (event) => {
                    event.stopPropagation();
                    const nextExpanded = !expandedCards[d.data.uuid];
                    pendingShowMoreFocusRef.current = {
                        mode: nextExpanded ? 'expand' : 'collapse',
                        targetId: d.data.uuid,
                    };
                    lastToggledCardRef.current = d.data.uuid;
                    setExpandedCards((prev) => ({
                        ...prev,
                        [d.data.uuid]: !prev[d.data.uuid],
                    }));
                });

                detailRows(d.data).forEach(([label, value], index) => {
                    const row = main
                        .append('g')
                        .attr('class', 'detail-row')
                        .attr('transform', `translate(0, ${134 + index * 24})`)
                        .style('opacity', isExpanded ? 1 : 0);

                    row.append('text')
                        .attr('class', 'detail-row-label')
                        .attr('x', 16)
                        .attr('fill', colors.textPrimary)
                        .attr('font-size', 12)
                        .attr('font-weight', 500)
                        .text(i18n.t(label));
                    row.append('text')
                        .attr('class', 'detail-row-value')
                        .attr('x', 284)
                        .attr('text-anchor', 'end')
                        .attr('fill', colors.textPrimary)
                        .attr('font-size', 12)
                        .attr('font-weight', 500)
                        .text(value);
                });
            });
        }

        node.filter((d) => !d.data.isNonNpd && isDepthLimitReached(d)).each(function(d) {
            const message = d3
                .select(this)
                .append('g')
                .attr('class', 'depth-limit-message')
                .attr(
                    'transform',
                    `translate(${graph.card.width / 2}, ${getNodeBottomOffset(
                        d,
                        graph.compact,
                        expandedCards,
                    ) + (graph.compact && d.data.properties?.isTaxGap ? 44 : 12)})`,
                )
                .style('cursor', 'default');
            message
                .append('foreignObject')
                .attr('x', -graph.card.width / 2)
                .attr('width', graph.card.width)
                .attr('height', 80)
                .append('xhtml:div')
                .style('text-align', 'center')
                .style('font-size', graph.compact ? '11px' : '24px')
                .style('line-height', '1.4')
                .style('color', colors.warningSecondary)
                .text(i18n.t('page_relations.tooltip.depth_limit', {maxLevel: MAX_VAT_TREE_LEVEL}));
        });

        root.eachBefore((d) => {
            d.data.x0 = d.x;
            d.data.y0 = d.renderY ?? d.y;
        });

        chartStateRef.current = {graph, root, links, duplicateLinks, node};

        const initialZoomTransform = zoomTransformRef.current ?? getDefaultZoomTransform(graph);
        graph.svg.call(graph.zoom.transform, initialZoomTransform);
        zoomTransformRef.current = initialZoomTransform;

        const branchFocus = pendingBranchFocusRef.current;
        if (branchFocus) {
            const targetNode = root
                .descendants()
                .find((item) => item.data.uuid === branchFocus.targetId);
            if (targetNode) {
                const focusX = graph.margin.left + targetNode.x + graph.card.width / 2;
                const focusY =
                    branchFocus.mode === 'expand'
                        ? graph.margin.top +
                          (targetNode.renderY ?? targetNode.y) +
                          getNodeBottomOffset(targetNode, graph.compact, expandedCards) +
                          graph.card.verticalSpace / 2
                        : graph.margin.top +
                          (targetNode.renderY ?? targetNode.y) +
                          getNodeTopOffset(targetNode, graph.compact) +
                          getNodeBottomOffset(targetNode, graph.compact, expandedCards) / 2;

                graph.svg
                    .transition()
                    .delay(220)
                    .duration(420)
                    .ease(d3.easeCubicOut)
                    .call(graph.zoom.translateTo, focusX, focusY);
            }
            pendingBranchFocusRef.current = null;
        }

        const nextSvg = graph.svg.node();
        container.append(nextSvg);
        previousSvg?.remove();

        return () => {
            chartStateRef.current = null;
            nextSvg?.remove();
        };
    }, [animateBranchCollapse, branchRevision, data, cardDirection, history, mode, onNodeToggle]);

    useEffect(() => {
        const state = chartStateRef.current;
        if (!state || state.graph.compact) return;

        const {graph, root, links, duplicateLinks, node} = state;
        const toggledId = lastToggledCardRef.current;

        if (!toggledId) return;

        applyExpandedLayout(root, expandedCards, graph.compact);
        const toggledNode = root.descendants().find((item) => item.data.uuid === toggledId);

        if (!toggledNode) return;

        const affectedIds = new Set<string | number>();
        toggledNode.each((item) => {
            affectedIds.add(item.data.uuid);
        });

        node.filter((d) => d.data.uuid === toggledId).each(function(d) {
            const isExpanded = Boolean(expandedCards[d.data.uuid]);
            d3.select(this)
                .select('g.depth-limit-message')
                .attr(
                    'transform',
                    `translate(${graph.card.width / 2}, ${getNodeBottomOffset(
                        d,
                        graph.compact,
                        expandedCards,
                    ) + 12})`,
                );
            const main = d3.select(this).select('g.main');
            if (main.empty()) return;

            main.select('rect.shadow-bg')
                .transition('show-more-card')
                .duration(SHOW_MORE_TRANSITION_MS)
                .ease(d3.easeCubicInOut)
                .attrTween('height', tweenNumberAttr('height', getDetailMainHeight(isExpanded)));
            main.select('rect.bg')
                .transition('show-more-card')
                .duration(SHOW_MORE_TRANSITION_MS)
                .ease(d3.easeCubicInOut)
                .attrTween('height', tweenNumberAttr('height', getDetailMainHeight(isExpanded)));
            main.select('text.card-toggle-link')
                .transition('show-more-card')
                .duration(SHOW_MORE_TRANSITION_MS)
                .ease(d3.easeCubicInOut)
                .style('opacity', 0.85)
                .attr('transform', `translate(150, ${isExpanded ? 280 : 150})`)
                .on('end', function() {
                    d3.select(this)
                        .text(
                            i18n.t(
                                isExpanded
                                    ? 'page_relations.card.hide'
                                    : 'page_relations.card.show_more',
                            ),
                        )
                        .style('opacity', 1);
                });

            main.selectAll('g.detail-row')
                .transition('show-more-rows')
                .duration(SHOW_MORE_TRANSITION_MS)
                .ease(d3.easeCubicInOut)
                .delay(isExpanded ? 90 : 0)
                .attr(
                    'transform',
                    (_, index) => `translate(0, ${134 + index * 24 + (isExpanded ? 0 : 8)})`,
                )
                .style('opacity', isExpanded ? 1 : 0);
        });

        node.filter((d) => affectedIds.has(d.data.uuid) && d.data.uuid !== toggledId).attr(
            'transform',
            (d) =>
                `translate(${graph.margin.left + d.x}, ${graph.margin.top + (d.renderY ?? d.y)})`,
        );

        links
            .filter((d) => affectedIds.has(d.target.data.uuid))
            .attr('d', (d) => getFinalLinkPath({graph, link: d, expandedCards}));

        duplicateLinks
            .filter(
                (d) => affectedIds.has(d.source.data.uuid) || affectedIds.has(d.target.data.uuid),
            )
            .attr('d', (d) =>
                createDuplicateLinkPath({
                    graph,
                    source: d.source,
                    target: d.target,
                    expandedCards,
                }),
            );

        if (pendingShowMoreFocusRef.current?.targetId === toggledId) {
            graph.svg.interrupt();
            const focusX = graph.margin.left + toggledNode.x + graph.card.width / 2;
            const focusY =
                graph.margin.top +
                (toggledNode.renderY ?? toggledNode.y) +
                getNodeTopOffset(toggledNode, graph.compact) +
                getNodeBottomOffset(toggledNode, graph.compact, expandedCards) / 2;

            graph.svg
                .transition()
                .delay(140)
                .duration(420)
                .ease(d3.easeCubicOut)
                .call(graph.zoom.translateTo, focusX, focusY, [
                    graph.width / 2,
                    graph.height * 0.42,
                ]);
        }

        root.eachBefore((d) => {
            d.data.y0 = d.renderY ?? d.y;
        });

        pendingShowMoreFocusRef.current = null;
        lastToggledCardRef.current = null;
    }, [expandedCards]);

    return (
        <Wrapper ref={containerRef}>
            {data && (
                <button
                    className="chart-toggle"
                    type="button"
                    onClick={() => setMode((prev) => (prev === 'simple' ? 'detail' : 'simple'))}
                >
                    {i18n.t(
                        mode === 'simple'
                            ? 'page_relations.view.simple'
                            : 'page_relations.view.detail',
                    )}
                </button>
            )}
        </Wrapper>
    );
};
